import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Banner, Button, Checkbox, Dialog, Skeleton } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { queryRoots } from '../../../lib/events';
import { putListSetters, updateList, useListDetail, type EventTypeRef } from '../api';
import { EventTypeCombobox } from '../EventTypeCombobox';
import styles from './lists.module.css';

/**
 * „Setter und Ereignistyp" — switching a list on asks for its setters and its event type (Terminierung 04
 * note; the dialog itself has no frame): setters from the dialer users of the organization, the event type
 * prefilled with the list's; saving stores both and switches the list on.
 */
export function ActivateListDialog({
  listId,
  onClose,
  onDone,
}: {
  listId: string | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const detail = useListDetail(listId ?? '');
  const [setters, setSetters] = useState<string[]>([]);
  const [eventType, setEventType] = useState<EventTypeRef | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const data = listId ? detail.data : undefined;

  useEffect(() => {
    if (!data) return;
    setSetters(data.setters.map((setter) => setter.id));
    setEventType(data.eventType);
    setError(null);
  }, [data]);

  async function save() {
    if (!listId) return;
    if (setters.length === 0 || eventType === null) {
      setError('Bitte wählen Sie Setter und Ereignistyp.');
      return;
    }
    setPending(true);
    setError(null);
    try {
      await putListSetters(listId, setters);
      await updateList(listId, { eventTypeId: eventType.id, active: true });
      await queryClient.invalidateQueries({ queryKey: [...queryRoots.dialer] });
      onDone();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog
      open={listId !== null}
      onClose={onClose}
      eyebrow="Dialer-Liste einschalten"
      title="Setter und Ereignistyp"
      subtitle={data?.name}
      width={560}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button loading={pending} onClick={() => void save()}>
            Einschalten
          </Button>
        </>
      }
    >
      <div className={styles.dialogBody}>
        {error ? <Banner tone="danger">{error}</Banner> : null}
        {!data ? (
          <Skeleton width="100%" height={120} />
        ) : (
          <>
            <div className={styles.dialogField}>
              <span className={styles.dialogLabel}>
                Ereignistyp für Termine aus dieser Liste<span className={styles.required}> *</span>
              </span>
              <EventTypeCombobox
                contactId={null}
                value={eventType}
                onChange={setEventType}
                label="Ereignistyp für Termine aus dieser Liste"
              />
            </div>
            <fieldset className={styles.dialogFieldset}>
              <legend className={styles.dialogLabel}>
                Setter auf dieser Liste<span className={styles.required}> *</span>
              </legend>
              {data.setterOptions.length === 0 ? (
                <p className={styles.dialogHint}>Niemand in Ihrer Organisation hat Zugriff auf den Dialer.</p>
              ) : (
                <div className={styles.setterChoices}>
                  {data.setterOptions.map((option) => (
                    <Checkbox
                      key={option.id}
                      label={option.name}
                      checked={setters.includes(option.id)}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        setSetters((current) =>
                          checked ? [...current, option.id] : current.filter((id) => id !== option.id),
                        );
                      }}
                    />
                  ))}
                </div>
              )}
            </fieldset>
          </>
        )}
      </div>
    </Dialog>
  );
}
